import { Gluejar } from '@charliewilco/gluejar';
import { Alert, Link } from '@mm/components';
import { landmineNames } from '@mm/constants/landmine-names';
import { GATEWAY_TIMEOUT } from '@mm/constants/status-codes';
import { EmptySetIcon, HeartIcon } from '@mm/icons';
import LiveChatCRUDActions from '@mm/state/actions/LiveChatCRUDActions';
import {
  uploadFile,
  uploadImage,
  uploadVideo,
} from '@mm/state/actions/uploadActions';
import commonHandler, {
  getErrorText,
  ResponseOrError,
} from '@mm/state/errorHandlers/common-handler';
import { getPlanInfo } from '@mm/state/helpers/billing-info-helpers';
import { identifyChannel } from '@mm/state/helpers/live-chat-helpers';
import { PageBillingType } from '@mm/state/interfaces/connected-page';
import { ContactMessage } from '@mm/state/model/ContactMessageModel';
import { LiveChatChannel } from '@mm/state/model/LivechatConversationModel';
import { ConversationDirection } from '@mm/state/model/LivechatMessageModel';
import PageModel from '@mm/state/model/PageModel';
import { PersonaModel } from '@mm/state/model/PersonaModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { selectActiveUserId } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { useActor } from '@xstate/react';
import { ReactComponent as AttachmentIcon } from 'assets/attachment-icon.svg';
import { ReactComponent as BlueWarning } from 'assets/icons/blue-warning.svg';
import { ReactComponent as RedWarning } from 'assets/warning.svg';
import classnames from 'classnames';
import { invoke } from 'lodash';
import moment from 'moment';
import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDropzone } from 'react-dropzone';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import { useContextSelector } from 'use-context-selector';
import useRouter from 'use-react-router';
import {
  ButtonColor,
  PrimaryButton,
} from '../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { getUpdatedTextWithEmoji } from '../../../../../common/components/EmojiPicker/emoji-picker-helpers';
import { EmojiPicker } from '../../../../../common/components/EmojiPicker/EmojiPicker';
import { getFormatFromType } from '../../../../../common/helpers/widget-helpers/use-common-widget-handlers';
import { useBotChannels } from '../../../../../common/hooks/use-bot-channels';
import { useFreeTrialUpgradeModal } from '../../../../../common/modals/FreeTrialUpgradeModal/use-free-trial-upgrade-modal';
import { ATTACHMENT_EXTENSIONS } from '../../../../../common/widgets/AttachmentWidget/constants';
import { VIDEO_EXTENSIONS } from '../../../../../common/widgets/VideoWidget/constants';
import { SettingsRoutes } from '../../../../../routes/settings-routes';
import { useInactiveBotActivator } from '../../../../common/hooks/use-inactive-bot-activator';
import { ContactContext, EmptySelectedContact } from '../../contactMachine';
import { ConversationContext } from '../../conversationMachine';
import { PersonaContext } from '../../HelperContexts';
import { LiveChatContext } from '../../LiveChatContext';
import { messageStructureParser } from '../Messages/messageStructureParser';
import { BotPausing } from './BotPausing';
import { DialogueContainingTextInput } from './DialogueContainingTextInput';
import { ExtraActionsButton } from './ExtraActionsButton';
import './MessageSender.pcss';
import { SenderSelector } from './SenderSelector';

const defaultPlaceholder = 'Write a message';
const sendingPlaceholder = 'Sending...';
const errorPlaceholder = 'Error while sending the message...';

const ACCEPTED_UPLOAD_EXTENSIONS = [
  '.jpg',
  '.jpeg',
  '.gif',
  '.png',
  ...VIDEO_EXTENSIONS,
  ...ATTACHMENT_EXTENSIONS,
];
const UPLOAD_SIZE_LIMIT = 25 * 1024 * 1024;

const getPlaceholder = (sending: boolean, error: boolean) => {
  return (
    (sending && sendingPlaceholder) ||
    (error && errorPlaceholder) ||
    defaultPlaceholder
  );
};

const renderPausedArea = (
  pausedText: string,
  chatbotIsActive: boolean | undefined,
  activateInactiveBot: () => void,
  isContactChannelActive: boolean | null,
  activateChannel: () => void,
  channel: LiveChatChannel | undefined,
) => {
  return !chatbotIsActive || !isContactChannelActive ? (
    <div className="live-chat--message-sender__bot-inactive-warning">
      <EmptySetIcon />
      <div>{pausedText}</div>
      {!chatbotIsActive && (
        <PrimaryButton
          color={ButtonColor.secondaryBlue}
          onClick={() => activateInactiveBot()}
        >
          Activate this bot
        </PrimaryButton>
      )}
      {!isContactChannelActive && channel !== 'email' && chatbotIsActive && (
        <PrimaryButton
          color={ButtonColor.secondaryBlue}
          onClick={activateChannel}
        >
          Activate this channel
        </PrimaryButton>
      )}
    </div>
  ) : (
    <BotPausing />
  );
};

// tslint:disable-next-line: cyclomatic-complexity
export const MessageSender = () => {
  const currentPersona = useContextSelector(
    PersonaContext,
    v => v.currentPersona,
  );

  const lcService = useContextSelector(
    LiveChatContext,
    v => v.lcServiceRef.current!,
  );

  const {
    hasError,
    message,
    memoizedOnChangeText,
    onDialogueSelected,
    sending,
    showWarning,
    textareaRef,
    onEmojiPicked,
    getDropzoneRootProps,
    getDropzoneInputProps,
    openDropzoneDialog,
    isDragActive,
    uploadedFile,
    setUploadedFile,
    rejectedFile,
    setRejectedFile,
    canSend,
    usePersona,
    setUsePersona,
    channel,
    setPastedImageURL,
    pastedImageURL,
    setMessage,
    conversationSessionSource,
    launchDialogue,
    selectedDialogue,
    setSelectedDialogue,
    channelDisconnected,
  } = useSendMessage(currentPersona);

  const {
    isActiveChatbot: chatbotIsActive,
    onClickActivate: activateInactiveBot,
  } = useInactiveBotActivator();

  const { activateChannelGuard, isChannelActive } = useBotChannels();
  const isContactChannelActive =
    channel === 'webchat'
      ? isChannelActive('webChat')
      : channel
      ? isChannelActive(channel)
      : null;

  const activateChannel = () => {
    if (channel) {
      if (channel === 'webchat') {
        activateChannelGuard('webChat');
      } else {
        activateChannelGuard(channel);
      }
    }
  };
  const pausedText = !chatbotIsActive
    ? 'This bot is inactive'
    : 'This channel is inactive';

  const dispatch: Dispatch<StoreState> = useDispatch();

  const [lcMachineState] = useActor(lcService);

  const { remoteUserId } = lcMachineState.context || {};

  const conversationMachine = remoteUserId
    ? lcMachineState.context.conversationsRefs[remoteUserId]
    : undefined;

  if (!conversationMachine) {
    return null;
  }

  const isDisabled =
    !remoteUserId ||
    sending ||
    hasError ||
    showWarning === '7d' ||
    showWarning === 'imported_contact' ||
    showWarning === 'instagram_empty_contact' ||
    !canSend ||
    channelDisconnected;

  const showPersonaSelector =
    channel !== 'sms' && channel !== 'instagram' && channel !== 'email';

  const composeAreaClass = classnames(
    'live-chat--message-sender__compose-area',
    {
      'live-chat--message-sender__compose-area__disabled': isDisabled,
    },
  );

  const textareaClass = classnames('live-chat--message-sender-textarea', {
    'live-chat--message-sender-textarea__disabled': isDisabled,
  });
  const btnTitle = sending ? sendingPlaceholder : 'Send';

  const setRef = ref => {
    textareaRef.current = ref;
  };

  const numberOfTextMessages = Math.ceil(message.length / 160);

  const messageCounterLimitText =
    channel === 'sms' && message.length > 160
      ? `. You reached  limit. ${numberOfTextMessages} text messages will be sent.`
      : '';

  const showLaunchDialogue = remoteUserId && !remoteUserId.startsWith('em_');

  return (
    <div className="live-chat--message-sender">
      {textareaRef?.current && (
        <Gluejar
          container={textareaRef.current}
          onPaste={async files => {
            try {
              if (files.images[0]) {
                const blobURL = files.images[0];
                if (pastedImageURL !== blobURL) {
                  const imageDefaultName = new Date().getTime().toString();
                  setPastedImageURL(blobURL);
                  const blobResponse = await fetch(blobURL);
                  const blob = await blobResponse.blob();
                  const value = textareaRef.current?.value ?? '';
                  const fileName = value.replace(message, '');
                  const file = new File(
                    [blob],
                    fileName ? fileName : imageDefaultName,
                    { type: blob.type },
                  );
                  setMessage(message);
                  setUploadedFile(file);
                  files.images = [];
                }
              }
            } catch (e) {
              await commonHandler(dispatch)(e);
            }
          }}
          onError={() => {}}
        />
      )}
      {remoteUserId && conversationSessionSource && (
        <div className="live-chat--message-sender__conversation-session-source">
          Conversation Started from:
          <div className="live-chat--message-sender__conversation-session-source-type">
            {conversationSessionSource}
          </div>
        </div>
      )}
      {remoteUserId && showWarning && (
        <div
          className={`live-chat--message-sender__warning-container-${showWarning}`}
        >
          {showWarning === '24h' ? (
            <>
              <BlueWarning className="live-chat--message-sender__warning-icon-24h" />
              This contact last interacted with your business more than 24 hours
              ago &nbsp;
              <a
                href="https://customers.ai/help/article/lcaqx1vg2t-inbox-24-hour-warning"
                target="_blank"
                rel="noopener noreferrer"
              >
                <i className="far fa-question-circle live-chat--message-sender__warning-question" />
              </a>
            </>
          ) : showWarning === 'imported_contact' ? (
            <Alert
              showCloseIcon={false}
              hideLeftIcon
              css={{ container: 'live-chat--message-sender__warning-alert' }}
            >
              We imported this contact from your FB Messenger history. The
              messages are not available to us.
            </Alert>
          ) : showWarning === 'instagram_empty_contact' ? (
            <Alert
              showCloseIcon={false}
              hideLeftIcon
              css={{ container: 'live-chat--message-sender__warning-alert' }}
            >
              We sent this contact a reply to a Comment or Story Mention. They
              are anonymous until they message you back.
            </Alert>
          ) : (
            <>
              <RedWarning className="live-chat--message-sender__warning-icon-7d" />
              This contact last interacted with your business more than 7-days
              ago &nbsp;
              <a
                href="https://customers.ai/help/article/ryihdl5n94"
                target="_blank"
                rel="noopener noreferrer"
              >
                <i className="far fa-question-circle live-chat--message-sender__warning-question" />
              </a>
            </>
          )}
        </div>
      )}

      <div className={composeAreaClass} {...getDropzoneRootProps()}>
        <input {...getDropzoneInputProps()} />
        {isDragActive && (
          <div className="live-chat--message-sender__drag-overlay">
            Drop file here
          </div>
        )}
        <DialogueContainingTextInput
          inputProps={{
            value: message,
            placeholder: getPlaceholder(sending, hasError),
            rows: 3,
            disabled: isDisabled,
            onChange: memoizedOnChangeText,
            className: textareaClass,
          }}
          setRef={setRef}
          textareaRef={textareaRef}
          setMessage={setMessage}
          onDialogueSelected={onDialogueSelected}
          launchDialogue={launchDialogue}
          selectedDialogue={selectedDialogue}
          setSelectedDialogue={setSelectedDialogue}
        />
        {uploadedFile && (
          <div className="live-chat--message-sender__attachment-preview">
            <AttachmentIcon />
            <div className="live-chat--message-sender__attachment-file-name">
              {uploadedFile.name}
            </div>
            <button
              className="live-chat--message-sender__attachment-remove-button"
              onClick={() => setUploadedFile(undefined)}
            >
              <i className="fas fa-times" />
            </button>
          </div>
        )}
        {rejectedFile && (
          <div className="live-chat--message-sender__attachment-preview live-chat--message-sender__attachment-preview--error">
            <i className="fas fa-ban" />
            <div className="live-chat--message-sender__attachment-file-name">
              {rejectedFile.name}
              {rejectedFile.size >= UPLOAD_SIZE_LIMIT
                ? ' (File Size Exceeds 25MB)'
                : ' (Unaccepted File Type)'}
            </div>
            <button
              className="live-chat--message-sender__attachment-remove-button"
              onClick={() => setRejectedFile(undefined)}
            >
              <i className="fas fa-times" />
            </button>
          </div>
        )}
        <div className="live-chat--message-sender__compose-toolbar-row">
          {showPersonaSelector && (
            <SenderSelector
              usePersona={usePersona}
              setUsePersona={setUsePersona}
              isDisabled={isDisabled}
            />
          )}
          <div className="live-chat--message-sender__compose-toolbar-buttons">
            {message.length > 0 && (
              <div
                className={classnames(
                  'live-chat--message-sender__compose-toolbar-chars-counter',
                  {
                    'live-chat--message-sender__compose-toolbar-chars-counter-red-message':
                      channel === 'sms' && message.length > 160,
                  },
                )}
              >
                {message.length}{' '}
                {message.length === 1
                  ? ' character'
                  : ` characters${messageCounterLimitText}`}
              </div>
            )}
            {showLaunchDialogue && (
              <ExtraActionsButton
                onDialogueSelected={onDialogueSelected}
                buttonClassName="live-chat--message-sender__compose-toolbar-button"
              />
            )}

            <div
              className="live-chat--message-sender__compose-toolbar-button"
              onClick={openDropzoneDialog}
            >
              <AttachmentIcon />
            </div>
            <EmojiPicker
              className="live-chat--message-sender__compose-toolbar-emoji-picker"
              classNameForIcon="live-chat--message-sender__compose-toolbar-emoji-picker-icon"
              onEmojiPicked={onEmojiPicked}
              additionalDropdownStyles={{
                bottom: '25px',
                top: 'auto',
              }}
            />
            {channel === 'instagram' && (
              <div
                className="live-chat--message-sender__compose-toolbar-button"
                onClick={e => onDialogueSelected(e, undefined, true)}
              >
                <HeartIcon />
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="live-chat--bottom-row-container">
        {renderPausedArea(
          pausedText,
          chatbotIsActive,
          activateInactiveBot,
          isContactChannelActive,
          activateChannel,
          channel,
        )}
        <PrimaryButton
          disabled={
            isDisabled || (!uploadedFile && message.trim().length === 0)
          }
          onClick={onDialogueSelected}
          className="live-chat--message-sender-btn"
        >
          {btnTitle}
        </PrimaryButton>
      </div>
    </div>
  );
};

export const useSendMessage = (currentPersona?: PersonaModel) => {
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [hasError, setError] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | undefined>();
  const [pastedImageURL, setPastedImageURL] = useState('');
  const [rejectedFile, setRejectedFile] = useState<File | undefined>();
  const [canSend, setCanSend] = useState(false);
  const [usePersonaSelection, setUsePersonaSelection] = useState<
    boolean | undefined
  >();

  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const lcService = useContextSelector(
    LiveChatContext,
    v => v.lcServiceRef.current!,
  );

  const { history } = useRouter();
  const activeUserId = useSelector(selectActiveUserId);
  const dispatch: Dispatch<StoreState> = useDispatch();
  const facebookPage = useSelector(activeFacebookPageSelector);
  const { openGeneralConfirmModal, openUniversalUpgradeLandmineModal } =
    useModalActions();
  const { openFreeTrialUpgradeModalPromotional } = useFreeTrialUpgradeModal();

  const [lcMachineState, lcMachineSend] = useActor(lcService);

  const { remoteUserId, remoteFbPageId } = lcMachineState.context || {};

  const selectedContact =
    (lcMachineState.context.remoteUserId
      ? (
          lcMachineState.context.contactsRefs[
            lcMachineState.context.remoteUserId
          ]?.state.context as ContactContext
        )?.model
      : undefined) || EmptySelectedContact;

  const channel = remoteUserId ? identifyChannel(remoteUserId) : undefined;
  const channelDisconnected = !!selectedContact.channelDisconnected;

  const selectedContactMachine = remoteUserId
    ? lcMachineState.context.contactsRefs[remoteUserId]
    : undefined;

  const refreshSelectedContact = useCallback(() => {
    selectedContactMachine?.send({
      type: 'LC_CONTACT_REFRESH',
      payload: remoteUserId || '',
    });
  }, [selectedContactMachine?.id, remoteUserId]);

  const conversationMachine = remoteUserId
    ? lcMachineState.context.conversationsRefs[remoteUserId]
    : undefined;
  const messages =
    (conversationMachine?.state.context as ConversationContext)?.allMessages ||
    [];

  const hasNativeIntegrationsFeature = useSelector(
    combinedFeatureFlagsSelector,
  ).nativeIntegrations;
  const hasActivateNumberFlag = useSelector(
    combinedFeatureFlagsSelector,
  ).smsActivateNumber;

  useEffect(() => {
    if (conversationMachine) {
      setCanSend(true);
    }
  }, [!!conversationMachine]);

  useEffect(() => {
    setUsePersonaSelection(undefined);
  }, [remoteUserId, setUsePersonaSelection]);

  const usePersona =
    usePersonaSelection ??
    selectedContact?.userPreference?.usePersona ??
    selectedContact?.conversation?.assignedUserId === activeUserId;

  const memoizedOnChangeText = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => setMessage(e.target.value),
    [setMessage],
  );

  const [selectedDialogue, setSelectedDialogue] = useState<PageModel | null>(
    null,
  );

  const updateFailedOptimisticMessage = async (
    error: ResponseOrError,
    optimisticMessage: ContactMessage,
  ) => {
    const errorMessage = await getErrorText(error);
    conversationMachine?.send({
      type: 'LC_UPDATE_OPTIMISTIC_MESSAGE',
      payload: {
        ...optimisticMessage,
        error: errorMessage,
      },
    });
  };

  const basicOptimisticMessageStructure = () => ({
    clientMessageId: crypto.getRandomValues(new Uint8Array(16)).join(''),
    time: new Date(),
    remoteUserId: remoteUserId as string,
    conversationSessionId: messages[0]
      ? messages[0].conversationSessionId
      : undefined,
  });

  const sendMessage = async (
    message: string,
    personaId?: number,
    dialogueParams?: { id: number; name: string },
    likeHeart?: boolean,
  ) => {
    if (channel === 'email' && !hasNativeIntegrationsFeature) {
      openFreeTrialUpgradeModalPromotional({
        landmineName: landmineNames.INBOX_SEND_EMAIL_MESSAGE,
      });
      return;
    }
    if (channel === 'sms' && !hasActivateNumberFlag) {
      openFreeTrialUpgradeModalPromotional({
        landmineName: landmineNames.INBOX_SEND_SMS_MESSAGE,
      });
      return;
    }
    if (remoteFbPageId && remoteUserId) {
      const restSendMessage = (clientMessageId?: string) =>
        dispatch(
          LiveChatCRUDActions.sendMessage(
            remoteFbPageId as string,
            remoteUserId as string,
            message,
            personaId,
            dialogueParams?.id,
            likeHeart,
            clientMessageId,
          ),
        );
      let extendedStructure;
      if (likeHeart) {
        extendedStructure = { message: { reaction: 'like' as const } };
      } else if (dialogueParams) {
        extendedStructure = {
          message: {
            dialogueId: dialogueParams.id,
            dialogueName: dialogueParams.name,
          },
        };
      } else {
        extendedStructure = { message: { text: message } };
      }
      const optimisticMessage = {
        ...basicOptimisticMessageStructure(),
        ...extendedStructure,
      };
      conversationMachine?.send({
        type: 'LC_SEND_OPTIMISTICALLY',
        payload: optimisticMessage,
      });
      invoke(textareaRef, 'current.focus');

      try {
        const result = await restSendMessage(optimisticMessage.clientMessageId);

        if (conversationMachine?.state.context.model.inboxStatus !== 'main') {
          conversationMachine?.send({
            type: 'LC_CONVERSATION_REDOWNLOAD',
          });

          lcMachineSend({
            type: 'LC_CONVERSATIONS_REFILTER',
            payload: [optimisticMessage.remoteUserId],
          });
        }
        if (result?.messageId) {
          conversationMachine?.send({
            type: 'LC_UPDATE_OPTIMISTIC_MESSAGE',
            payload: {
              ...optimisticMessage,
              lcMessageId: result.messageId,
            },
          });
        }
        return;
      } catch (error) {
        await updateFailedOptimisticMessage(error, optimisticMessage);
      } finally {
        refreshSelectedContact();
      }
    }
  };

  useEffect(() => {
    if (remoteUserId && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [remoteUserId, textareaRef.current]);

  const onDialogueSelected = useCallback(
    async (
      e?: any,
      dialogueParams?: { id: number; name: string },
      likeHeart?: boolean,
    ) => {
      if (facebookPage?.hardLimitReached) {
        const planInfo = getPlanInfo(facebookPage);
        if (planInfo.isFreePlan) {
          openGeneralConfirmModal({
            headerText: 'Send Limit Exceeded',
            messageText:
              'You’ve exceeded your send limit this month. Upgrade your plan to continue sending messages.',
            okButtonText: 'Upgrade your plan',
            okButtonStyling: {
              blue: true,
            },
            onCloseHandler: ok => {
              if (ok) {
                openUniversalUpgradeLandmineModal({
                  landmineName: 'send_limit',
                  planFallback: PageBillingType.Pro,
                });
              }
            },
          });
        } else {
          openGeneralConfirmModal({
            headerText: 'Send Limit Exceeded',
            messageText:
              'You’ve exceeded your send limit this billing cycle. Upgrade to a higher send tier to continue sending messages.',
            okButtonText: 'Upgrade to higher tier',
            okButtonStyling: {
              blue: true,
            },

            onCloseHandler: ok => {
              if (ok) {
                history.push(SettingsRoutes.billing.getLink());
              }
            },
          });
        }
        return;
      }
      const trimmedMessage = message.trim();
      const localPersonaId = usePersona ? currentPersona?.id : undefined;
      try {
        if (selectedDialogue) {
          setMessage('');
          await sendMessage('', localPersonaId, {
            id: selectedDialogue.id,
            name: selectedDialogue.name,
          });
          setSelectedDialogue(null);
          return;
        }
        if (likeHeart) {
          await sendMessage('', localPersonaId, undefined, true);
        }
        if (dialogueParams) {
          await sendMessage('', localPersonaId, dialogueParams);
        }
        if (trimmedMessage || uploadedFile) {
          setMessage('');
          if (trimmedMessage) {
            await sendMessage(trimmedMessage, localPersonaId);
          }
          if (uploadedFile) {
            setUploadedFile(undefined);
            await sendAttachment(uploadedFile);
          }
        }
      } catch (e) {
        setError(true);
        // Reset previous value after error
        setTimeout(() => {
          setError(false);
          setMessage(trimmedMessage);
        }, 2000);
      }
    },
    [
      setMessage,
      message,
      setSending,
      sending,
      usePersona,
      currentPersona,
      uploadedFile,
      remoteUserId,
      selectedDialogue,
    ],
  );

  const launchDialogue = async (dialogueParams: {
    id: number;
    name: string;
  }) => {
    const localPersonaId = usePersona ? currentPersona?.id : undefined;
    await sendMessage('', localPersonaId, dialogueParams);
  };

  const showWarning = useMemo(() => {
    if (channel === 'sms' || channel === 'webchat' || channel === 'email') {
      return undefined;
    }
    if (selectedContact.id !== 0) {
      const checkBasedOnMessages = () => {
        const latestUserMessage = messages.find(
          m => m.direction === ConversationDirection.incoming,
        );
        if (latestUserMessage) {
          return Date.now() - Number(latestUserMessage.time) >=
            7 * 24 * 60 * 60 * 1000
            ? '7d'
            : Date.now() - Number(latestUserMessage.time) >= 24 * 60 * 60 * 1000
            ? '24h'
            : undefined;
        }
      };

      const checkBasedOnLastActive = () => {
        if (!selectedContact || !selectedContact.id) {
          return undefined;
        }
        const lastActiveVariable = selectedContact.variables.find(
          v => v.variable.name === 'last active',
        );
        if (!lastActiveVariable) {
          return undefined;
        }
        const parsedTime = moment.utc(
          lastActiveVariable.value,
          'YYYY-MM-DD HH:mm:ss UTC',
          true,
        );
        if (!parsedTime.isValid()) {
          return undefined;
        }
        return parsedTime.add(7, 'days').isBefore(moment())
          ? '7d'
          : parsedTime.add(1, 'days').isBefore(moment())
          ? '24h'
          : undefined;
      };

      const checkEmptyConvos = () => {
        if (messages.length === 0) {
          if (channel === 'facebook') {
            return 'imported_contact';
          }
          if (channel === 'instagram') {
            return 'instagram_empty_contact';
          }
          return undefined;
        }
      };

      return checkBasedOnLastActive() !== undefined
        ? checkBasedOnLastActive()
        : messages.length > 0
        ? checkBasedOnMessages()
        : checkEmptyConvos();
    }
  }, [messages, selectedContact]);

  const onEmojiPicked = (emoji: string) => {
    const caretPos = textareaRef.current
      ? textareaRef.current.selectionStart
      : undefined;
    setMessage(getUpdatedTextWithEmoji(message, emoji, textareaRef.current));
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        if (caretPos !== undefined) {
          textareaRef.current.selectionStart = caretPos + emoji.length;
          textareaRef.current.selectionEnd = caretPos + emoji.length;
        }
      }
    }, 1);
  };

  const {
    getRootProps: getDropzoneRootProps,
    getInputProps: getDropzoneInputProps,
    open: openDropzoneDialog,
    isDragActive,
  } = useDropzone({
    accept: ACCEPTED_UPLOAD_EXTENSIONS,
    noClick: true,
    maxSize: UPLOAD_SIZE_LIMIT,
    onDrop: (files: File[], rejectedFiles: File[]) => {
      if (files.length) {
        setUploadedFile(files[0]);
        setRejectedFile(undefined);
      } else if (rejectedFiles.length) {
        setRejectedFile(rejectedFiles[0]);
        setUploadedFile(undefined);
      }
    },
  });

  const sendAttachment = async (file: File) => {
    const getUploadMethod = () => {
      if (file.type.startsWith('image/')) {
        setPastedImageURL('');
        return uploadImage;
      }
      if (file.type.startsWith('video/')) {
        return uploadVideo;
      }
      return uploadFile;
    };
    const optimisticMessage = {
      ...basicOptimisticMessageStructure(),
      message: { file },
    };

    conversationMachine?.send({
      type: 'LC_SEND_OPTIMISTICALLY',
      payload: optimisticMessage,
    });
    invoke(textareaRef, 'current.focus');
    try {
      const assetId = await dispatch(
        getUploadMethod()({
          content: file,
          extension: getFormatFromType(file.type),
        }),
      );

      const updatedMessage = {
        ...optimisticMessage,
        message: { ...optimisticMessage.message, assetId },
      };

      conversationMachine?.send({
        type: 'LC_UPDATE_OPTIMISTIC_MESSAGE',
        payload: updatedMessage,
      });

      try {
        const result = await dispatch(
          LiveChatCRUDActions.sendAsset(
            remoteFbPageId as string,
            remoteUserId as string,
            assetId,
            usePersona ? currentPersona?.id : undefined,
            optimisticMessage.clientMessageId,
          ),
        );

        if (conversationMachine?.state.context.model.inboxStatus !== 'main') {
          conversationMachine?.send({
            type: 'LC_CONVERSATION_REDOWNLOAD',
          });

          lcMachineSend({
            type: 'LC_CONVERSATIONS_REFILTER',
            payload: [updatedMessage.remoteUserId],
          });
        }
        if (result?.messageId) {
          conversationMachine?.send({
            type: 'LC_UPDATE_OPTIMISTIC_MESSAGE',
            payload: {
              ...updatedMessage,
              lcMessageId: result.messageId,
            },
          });
        }
      } catch (error) {
        if (error instanceof Response && error.status === GATEWAY_TIMEOUT) {
          // 504 for media usually refers to Nginx gateway timeouts
          return;
        }
        await updateFailedOptimisticMessage(error, updatedMessage);
      } finally {
        refreshSelectedContact();
      }
      return;
    } catch (error) {
      await updateFailedOptimisticMessage(error, optimisticMessage);
      refreshSelectedContact();
    }
  };

  const conversationSessionSource = useMemo(() => {
    const mostRecentConversationId = messages[0]?.conversationSessionId;
    if (!mostRecentConversationId) {
      return;
    }
    const sessionMessages = messages.filter(
      m => m.conversationSessionId === mostRecentConversationId,
    );
    const firstMessage = sessionMessages[sessionMessages.length - 1];
    for (const conversationMessage of sessionMessages) {
      const { isChatWidget, getWebsiteChatUrl } =
        messageStructureParser(conversationMessage);
      const isFirstMessage = conversationMessage === firstMessage;
      if (isChatWidget(isFirstMessage)) {
        const siteUrl = getWebsiteChatUrl(isFirstMessage);
        if (siteUrl) {
          return (
            <>
              Website Chat{' '}
              <Link
                className="live-chat--message-sender__conversation-session-source-url"
                href={siteUrl.href}
                isBlank
              >
                {siteUrl.hostname}
                {siteUrl.pathname === '/' ? '' : siteUrl.pathname}
              </Link>
            </>
          );
        }
        return 'Website Chat';
      }
    }
  }, [JSON.stringify(messages)]);

  return {
    hasError,
    message,
    memoizedOnChangeText,
    onDialogueSelected,
    sending,
    showWarning,
    usePersona,
    setUsePersona: setUsePersonaSelection,
    textareaRef,
    onEmojiPicked,
    getDropzoneRootProps,
    getDropzoneInputProps,
    openDropzoneDialog,
    isDragActive,
    uploadedFile,
    setUploadedFile,
    rejectedFile,
    setRejectedFile,
    canSend,
    channel,
    pastedImageURL,
    setPastedImageURL,
    setMessage,
    messages,
    conversationSessionSource,
    launchDialogue,
    selectedDialogue,
    setSelectedDialogue,
    channelDisconnected,
  };
};
